import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaretLeft, CaretRight, MapTrifold, CalendarBlank } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import {
  MobileStopCard,
  cycleStop,
  KIND_ORDER,
  todayIso,
  phoneKey,
} from "@/pages/mobile/MobileStop";

const monthOf = (iso) => iso.slice(0, 7);
const shiftDay = (iso, n) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return (
    new Date(y, m - 1, 1).toLocaleString("en-US", { month: "short" }) + ", " + y
  );
};

export default function MobileCalendar() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(todayIso());
  const [events, setEvents] = useState([]);
  const [unreadKeys, setUnreadKeys] = useState(new Set());
  const stripRef = useRef(null);
  const ym = monthOf(selected);
  const today = todayIso();

  const load = useCallback(async () => {
    const { data } = await api.get("/jobs/schedule", { params: { month: ym } });
    setEvents(data.events || []);
  }, [ym]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (stripRef.current) stripRef.current.scrollLeft = 0;
  }, [selected]);
  useEffect(() => {
    api
      .get("/sms/unread")
      .then(({ data }) =>
        setUnreadKeys(
          new Set((data.items || []).map((m) => phoneKey(m.from)).filter(Boolean)),
        ),
      )
      .catch(() => {});
  }, []);

  const dotDays = useMemo(() => new Set(events.map((e) => e.date)), [events]);
  const [showDone, setShowDone] = useState(() => localStorage.getItem("mobile_show_done") === "1");
  useEffect(() => {
    localStorage.setItem("mobile_show_done", showDone ? "1" : "0");
  }, [showDone]);
  const dayStops = useMemo(
    () =>
      events
        .filter((e) => e.date === selected)
        .filter((e) => showDone || !(e.entity !== "service" && e.stage === "Complete"))
        .sort((a, b) => (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9)),
    [events, selected, showDone],
  );
  const hiddenDone = useMemo(
    () => events.filter((e) => e.date === selected && e.entity !== "service" && e.stage === "Complete").length,
    [events, selected],
  );

  const apply = (stop, next) =>
    setEvents((list) =>
      list.map((e) =>
        e.job_id === stop.job_id &&
        e.kind === stop.kind &&
        (e.stop_id || "") === (stop.stop_id || "")
          ? { ...e, stop_status: { ...e.stop_status, status: next } }
          : e,
      ),
    );

  return (
    <div className="px-4 pt-5" data-testid="mobile-calendar">
      <h1 className="m-title m-display text-3xl text-[#c39e6d]">Schedule</h1>
      <div className="mt-3 flex items-center justify-between">
        <div className="text-base font-bold text-zinc-100">
          {monthLabel(ym)}
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="mobile-cal-prev-day"
            onClick={() => setSelected(shiftDay(selected, -1))}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 hover:bg-zinc-800"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
          <button
            data-testid="mobile-cal-today"
            onClick={() => setSelected(todayIso())}
            className="h-9 rounded-lg border border-[#c39e6d] bg-[#c39e6d] px-3 text-xs font-bold uppercase tracking-widest text-slate-950"
          >
            Today
          </button>
          <button
            data-testid="mobile-cal-next-day"
            onClick={() => setSelected(shiftDay(selected, 1))}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 hover:bg-zinc-800"
          >
            <CaretRight size={16} weight="bold" />
          </button>
        </div>
      </div>

      <div
        ref={stripRef}
        className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1"
        data-testid="mobile-calendar-strip"
      >
        {Array.from({ length: 15 }, (_, i) => shiftDay(selected, i - 3)).map((iso) => {
          const dd = new Date(iso + "T00:00:00");
          const isSel = iso === selected;
          return (
            <button
              key={iso}
              data-testid={`mobile-cal-day-${iso}`}
              onClick={() => setSelected(iso)}
              className={`relative flex h-20 w-14 shrink-0 flex-col items-center justify-center gap-1 rounded-2xl border transition-all duration-150 active:scale-95 ${
                isSel
                  ? "border-[#c39e6d] bg-[#c39e6d] shadow-md shadow-[#c39e6d]/20"
                  : iso === today
                    ? "border-[#c39e6d]/50 bg-[#0B1220]"
                    : "border-slate-800 bg-[#0B1220]"
              }`}
            >
              <span
                className={`text-[10px] font-bold uppercase tracking-wider ${
                  isSel ? "text-slate-900" : "text-slate-500"
                }`}
              >
                {dd.toLocaleDateString("en-US", { weekday: "short" })}
              </span>
              <span
                className={`text-xl font-extrabold ${isSel ? "text-slate-950" : "text-slate-100"}`}
              >
                {Number(iso.slice(8))}
              </span>
              {dotDays.has(iso) && (
                <span
                  className={`absolute bottom-2 h-1 w-1 rounded-full ${
                    isSel ? "bg-slate-900" : "bg-[#c39e6d]"
                  }`}
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-between">
        <div
          className="text-xs font-semibold uppercase tracking-widest text-zinc-500"
          data-testid="mobile-jobs-today"
        >
          Jobs {selected === today ? "today" : "on " + selected} (
          {dayStops.length})
          {hiddenDone > 0 && (
            <button
              type="button"
              data-testid="mobile-cal-toggle-done"
              onClick={() => setShowDone((v) => !v)}
              className="ml-2 rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] normal-case tracking-normal text-zinc-400"
            >
              {showDone ? "Hide" : "Show"} {hiddenDone} completed
            </button>
          )}
        </div>
        <button
          data-testid="mobile-cal-map-button"
          onClick={() => navigate("/mobile/map")}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-zinc-700 px-3 text-[11px] font-semibold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
        >
          <MapTrifold size={14} weight="bold" />
          Map
        </button>
      </div>

      <div className="mt-3 space-y-3">
        {dayStops.length === 0 && (
          <div
            data-testid="mobile-cal-empty"
            className="rounded-2xl border border-dashed border-zinc-800 p-8 text-center text-sm text-zinc-500"
          >
            Nothing scheduled.
          </div>
        )}
        {dayStops.map((s) => (
          <MobileStopCard
            key={`${s.job_id}-${s.kind}-${s.stop_id || ""}`}
            s={s}
            unread={unreadKeys.has(phoneKey(s.customer_phone))}
            onCycle={() => cycleStop(s, (next) => apply(s, next))}
            onReschedule={load}
          />
        ))}
      </div>
    </div>
  );
}
